<template>
  <div>
    <b-form-group>
      <div class="d-flex align-items-center">
        <div class="flex-grow-1">
          <AutoComplete
            ref="auto"
            v-model="internalValue"
            :label="label"
            :placeholder="placeholder"
            :serializer="serializer"
            param-query="search"
            endpoint="surgeryCategory"
            text-title="name"
          />
        </div>
        <b-button
          variant="success"
          size="sm"
          style="width: 35px; height: 35px; border-radius: 10%; display: flex; align-items: center; justify-content: center;"
          @click="showCreateModal"
        >
          +
        </b-button>
      </div>
      <p
        v-if="errorMessage"
        class="text-danger mt-1"
      >
        {{ errorMessage }}
      </p>
    </b-form-group>

    <b-modal
      v-model="showModal"
      :title="$t('surgery.surgery-category') || 'تصنيف العملية'"
      hide-footer
      centered
    >
      <b-form-group :label="$t('common.name')">
        <b-form-input
          v-model="newCategory.name"
          :placeholder="$t('common.name')"
        />
      </b-form-group>
      <b-form-group :label="$t('common.description')">
        <b-form-input
          v-model="newCategory.description"
          :placeholder="$t('common.description')"
        />
      </b-form-group>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="showModal = false"
        >
          {{ $t('common.cancel') || 'إلغاء' }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving"
          @click="createCategory"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
          />
          <span v-else>
            {{ $t('common.save') || 'حفظ' }}
          </span>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BButton,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AutoComplete from '@/components/common/AutoComplete.vue'

export default {
  name: 'SurgeryCategorySelect',
  components: {
    BFormGroup,
    BFormInput,
    BButton,
    BModal,
    BSpinner,
    AutoComplete,
    ToastificationContent,
  },
  props: {
    value: {
      type: [String, Number, Object, null],
      default: null,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default() {
        return this.$t('surgery.surgery-category') || 'تصنيف العملية'
      },
    },
    placeholder: {
      type: String,
      default() {
        return this.$t('surgery.surgery-category') || 'تصنيف العملية'
      },
    },
    serializer: {
      type: Function,
      default: val => (val ? val.id : null),
    },
  },
  data() {
    return {
      internalValue: this.value,
      showModal: false,
      saving: false,
      newCategory: {
        name: '',
        description: '',
      },
    }
  },
  watch: {
    value(newVal) {
      this.internalValue = newVal
    },
    internalValue(newVal) {
      this.$emit('input', newVal)
    },
  },
  methods: {
    showCreateModal() {
      this.newCategory = {
        name: '',
        description: '',
      }
      this.showModal = true
    },
    async createCategory() {
      if (!this.newCategory.name) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.validation-error') || 'خطأ في التحقق',
            text: `${this.$t('common.name')} ${this.$t('common.is-required') || 'حقل مطلوب'}`,
            variant: 'warning',
          },
        })
        return
      }

      this.saving = true
      const params = {
        form: {
          name: this.newCategory.name,
          description: this.newCategory.description,
        },
        path: '/surgeryCategory',
      }
      try {
        const response = await this.$store.dispatch('create', params)
        const created = (response && response.data) || response || params.form

        // Ensure the new category appears in the dropdown
        if (this.$refs.auto && typeof this.$refs.auto.addToItems === 'function') {
          this.$refs.auto.addToItems(created)
        }

        // Set the newly created category as the current selection
        const id = created.id || this.serializer(created)
        this.internalValue = id

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('common.success') || 'تم بنجاح',
            text: this.$t('common.saved-successfully') || 'تم الحفظ بنجاح',
            variant: 'success',
          },
        })
        this.showModal = false
      } catch (error) {
        if (error && error.response && error.response.status === 400 && error.response.data) {
          Object.keys(error.response.data).forEach(key => {
            const msgs = error.response.data[key]
            const msg = Array.isArray(msgs) ? msgs[0] : msgs
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'validation error',
                variant: 'danger',
                text: msg,
              },
            })
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: this.$t('common.error') || 'خطأ',
              variant: 'danger',
              text:
                (error && error.message)
                || this.$t('common.error-fetching-data')
                || 'حدث خطأ أثناء الحفظ',
            },
          })
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
